import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserLanguage,
  updateUserLanguage
} from "../services/languageService";
import {
  POST_LANG,
  POST_LANGUAGE_ERROR,
  POST_LANGUAGE_LOADING
} from "../store/action";
import {
  clearStoredLanguage,
  getInitialLanguage,
  saveLanguageToStorage
} from "../utils/languageUtils";
import { createLogger } from "../utils/logger";

const logger = createLogger("UseLanguage");

export const useLanguage = () => {
  const dispatch = useDispatch();
  const selectedLang = useSelector((state) => state.lang);
  const user = useSelector((state) => state.user);
  const languageLoading = useSelector((state) => state.languageLoading);
  const languageError = useSelector((state) => state.languageError);

  // Track if we've already loaded user language to prevent duplicate calls
  const hasLoadedUserLanguage = useRef(false);
  const lastUserId = useRef(null);

  // Initialize language on app start
  useEffect(() => {
    if (!selectedLang) {
      const initialLang = getInitialLanguage();
      dispatch({ type: POST_LANG, data: initialLang });
      saveLanguageToStorage(initialLang);
    }
  }, [selectedLang, dispatch]);

  // Load user language preference when user logs in (only once per user)
  useEffect(() => {
    const loadUserLanguage = async () => {
      // Only load if user exists, has changed, and we haven't loaded for this user yet
      if (user && user._id && user.refresh_token) {
        // Reset flag if user changed
        if (lastUserId.current !== user._id) {
          hasLoadedUserLanguage.current = false;
          lastUserId.current = user._id;
        }

        // Skip if already loaded for this user
        if (hasLoadedUserLanguage.current) {
          return;
        }

        // If languageLoading is stuck (shouldn't happen, but handle it to prevent deadlock)
        if (languageLoading) {
          logger.log("Language loading was stuck, resetting it");
          dispatch({ type: POST_LANGUAGE_LOADING, data: false });
        }

        try {
          dispatch({ type: POST_LANGUAGE_LOADING, data: true });
          const response = await getUserLanguage(user._id, user.refresh_token);

          // Only dispatch if language is different from current
          if (response.language && response.language !== selectedLang) {
            dispatch({ type: POST_LANG, data: response.language });
            saveLanguageToStorage(response.language);
          }

          hasLoadedUserLanguage.current = true;
        } catch (_error) {
          logger.log("No saved user language preference found, using default");
          // Only set language if we don't have one set yet
          if (!selectedLang) {
            const storedLang = getInitialLanguage();
            dispatch({ type: POST_LANG, data: storedLang });
            saveLanguageToStorage(storedLang);
          }
          // Don't set error state for expected "not found" cases
          hasLoadedUserLanguage.current = true;
        } finally {
          dispatch({ type: POST_LANGUAGE_LOADING, data: false });
        }
      } else {
        // Reset flag when user logs out
        hasLoadedUserLanguage.current = false;
        lastUserId.current = null;
        // Also reset languageLoading if it's stuck
        if (languageLoading) {
          dispatch({ type: POST_LANGUAGE_LOADING, data: false });
        }
      }
    };

    loadUserLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.refresh_token]); // selectedLang and languageLoading intentionally excluded to prevent loops

  // Change language function
  const changeLanguage = useCallback(
    async (newLanguage) => {
      if (!["en", "fr"].includes(newLanguage)) {
        logger.error("Invalid language:", newLanguage);
        return;
      }

      // Skip if already the same language
      if (newLanguage === selectedLang) {
        return;
      }

      // Prevent concurrent calls
      if (languageLoading) {
        logger.log("Language update already in progress, skipping");
        return;
      }

      const previousLang = selectedLang || "en";

      // Update local state immediately for better UX (only if different)
      if (newLanguage !== selectedLang) {
        dispatch({ type: POST_LANG, data: newLanguage });
        saveLanguageToStorage(newLanguage);
      }

      // Update backend if user is logged in
      if (user && user._id && user.refresh_token) {
        try {
          dispatch({ type: POST_LANGUAGE_LOADING, data: true });
          await updateUserLanguage(user._id, newLanguage, user.refresh_token);
          // Clear any previous errors on success
          if (languageError) {
            dispatch({ type: POST_LANGUAGE_ERROR, data: null });
          }
        } catch (error) {
          logger.error("Failed to update user language preference:", error);
          // Don't revert language change on error - keep the new language for better UX
          // Only set error if it's a real error (not just network timeout)
          if (error.message && !error.message.includes("timeout")) {
            dispatch({ type: POST_LANGUAGE_ERROR, data: error.message });
          }
        } finally {
          // Always reset loading state, even on error
          dispatch({ type: POST_LANGUAGE_LOADING, data: false });
        }
      } else {
        // If user is not logged in, ensure loading is false (no backend update needed)
        if (languageLoading) {
          dispatch({ type: POST_LANGUAGE_LOADING, data: false });
        }
      }
    },
    [user, selectedLang, languageLoading, languageError, dispatch]
  );

  // Clear language preference (on logout)
  const clearLanguage = useCallback(() => {
    clearStoredLanguage();
    dispatch({ type: POST_LANG, data: null });
  }, [dispatch]);

  return {
    selectedLang,
    languageLoading,
    languageError,
    changeLanguage,
    clearLanguage,
    isEnglish: selectedLang === "en",
    isFrench: selectedLang === "fr"
  };
};
